import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BaseTransaction, BaseellaStatus, Pagination, Response } from '@ui/src';
import { ExportBatchEntity } from '../../types';
import { exportPspApi } from '../services';
import { statusesTransactionsApi } from '../../../../store/statuses/services';

export interface ExportTransactionListState {
  transactions?: BaseTransaction[];
  transactionsPagination?: Pagination;
  selectedTransactions?: BaseTransaction[];
  selectedTransactionIds?: string[];
  sum?: number;
  sumSelected?: number;
  errorsList?: string;
}

const initialState: ExportTransactionListState = {
  transactions: undefined,
  selectedTransactionIds: undefined,
  sum: undefined,
  sumSelected: undefined,
};

export const exportTransactionListSlice = createSlice({
  name: 'transactionList',
  initialState,
  reducers: {
    onSelectedRows: (state, action: PayloadAction<string[]>) => {
      state.selectedTransactionIds = action.payload;

      let nextSelectedTransactions: BaseTransaction[] = [];

      if (state.selectedTransactions) {
        nextSelectedTransactions = ([] as BaseTransaction[]).concat(
          state.selectedTransactions,
        );
      }

      action.payload.forEach((item) => {
        if (
          !nextSelectedTransactions.find(
            (transaction) => transaction.id === item,
          )
        ) {
          const currentTransaction = state.transactions?.find(
            (transaction) => transaction.id === item,
          );

          if (currentTransaction) {
            nextSelectedTransactions.push(currentTransaction);
          }
        }
      });

      const deletedIds: string[] = [];

      nextSelectedTransactions?.forEach((item) => {
        if (!action.payload.find((id) => item.id === id)) {
          deletedIds.push(item.id);
        }
      });

      nextSelectedTransactions = nextSelectedTransactions?.filter((item) =>
        deletedIds.every((id) => item.id !== id),
      );

      state.selectedTransactions = nextSelectedTransactions;

      state.sumSelected = nextSelectedTransactions.reduce(
        (acc, curValue) => acc + curValue.sum,
        0,
      );
    },
    clearExportTransactionListState: (state) => {
      state.transactions = undefined;
      state.selectedTransactionIds = undefined;
      state.sum = undefined;
      state.sumSelected = undefined;
      state.transactionsPagination = undefined;
      state.errorsList = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        exportPspApi.endpoints.getExportOutgoingList.matchPending,
        (state) => {
          state.errorsList = undefined;
        },
      )
      .addMatcher(
        exportPspApi.endpoints.getExportOutgoingList.matchRejected,
        (state, action: any) => {
          state.transactions = undefined;
          state.transactionsPagination = undefined;
          state.sum = undefined;

          if (action?.payload.data) {
            if (Array.isArray(action?.payload.data?.message)) {
              state.errorsList = action?.payload.data?.message
                ?.map((item: { value: string }) => item.value)
                .join('; ');
            } else {
              state.errorsList = action?.payload.data?.error;
            }
          }
        },
      )
      .addMatcher(
        exportPspApi.endpoints.getExportOutgoingList.matchFulfilled,
        (state, action: PayloadAction<Response<BaseTransaction>>) => {
          const transactions = action.payload.data.edges?.map(
            (item) => item.node,
          );

          state.transactions = transactions;
          state.transactionsPagination = action.payload.data.pageInfo;
          state.sum = transactions.reduce(
            (acc, curValue) => acc + curValue.sum,
            0,
          );
        },
      )
      .addMatcher(
        exportPspApi.endpoints.getExportBatchById.matchFulfilled,
        (state, action: PayloadAction<{ data: ExportBatchEntity }>) => {
          const transactions = action.payload.data.transactions.edges?.map(
            (item) => item.node,
          );

          state.transactions = transactions;
          state.sum = transactions.reduce(
            (acc, curValue) => acc + curValue.sum,
            0,
          );
          state.transactionsPagination =
            action.payload.data.transactions.pageInfo;
        },
      )
      .addMatcher(
        statusesTransactionsApi.endpoints.changeTransactionStatus
          .matchFulfilled,
        (
          state,
          action: {
            meta: {
              arg: {
                originalArgs: { id: string };
              };
            };
          },
        ) => {
          if (state.transactions) {
            state.transactions = state.transactions.map((item) => {
              const result = { ...item };

              if (result.id === action.meta.arg.originalArgs.id) {
                result.baseellaStatus = BaseellaStatus.ARCH;
              }

              return result;
            });
          }
        },
      );
  },
});

export const { onSelectedRows, clearExportTransactionListState } =
  exportTransactionListSlice.actions;

export default exportTransactionListSlice.reducer;
